import {
  fetchAccountsPromise,
  getOffersPromise,
  removeNotOwnedOffersPromise,
  updateAccountsPromise,
  updateOfferPromise
} from 'app/app/(client)/db/db'
import { checkIfContractInactive } from 'app/app/common/common-utils'
import { getOpportunityIds } from 'app/app/common/opportunity'
// import { dispatch } from "app/redux/StoreProvider"
import { CustomerData } from 'app/types/backend-api'
import { Offer, SyncStatus } from 'app/types/types'
import { t } from 'i18next'
import { utc } from 'moment'
import * as R from 'ramda'
import { getCustomerData } from 'app/services/client/client-api'
import { setCurrentSync } from '../reduxSlices/current-sync-slice'
import { Args } from 'app/types/redux-types'
import { ThunkAPI } from '../../../redux/ReduxSlice'
import { withThunkHandler } from 'app/app/(client)/utils/async-handler'

const fetchCustomerDataHandler = async (_thunkAPI?: ThunkAPI, _args?: Args) => {
  const customers = await getCustomerData()
  return {
    customers,
    offers: []
  }
}

export const fetchCustomerDataAsync = withThunkHandler(fetchCustomerDataHandler)

export const initializeCustomerOfferInStores = async (data: CustomerData, dispatch: any) => {
  const { customers = [], offers = [] } = data
  const dataLoadState = customers.reduce(
    (acc, account) => {
      account.opportunities.forEach(opportunity => {
        const savedOffer = offers.find(o => o.opportunityId === opportunity.id)
        const isContractInactive = checkIfContractInactive(opportunity)
        let state
        if (savedOffer || opportunity.syncedWithSF) {
          state = { syncedWithSF: true, isSyncing: false, syncError: '' }
        } else if (isContractInactive) {
          state = {
            syncedWithSF: false,
            isSyncing: false,
            syncError: t('assessment.overview.initial.inactiveContract')
          }
        } else {
          state = { syncedWithSF: false, isSyncing: false, syncError: '' }
        }
        acc[opportunity.id] = state
      })
      return acc
    },
    {} as Record<string, SyncStatus>
  )
  dispatch(setCurrentSync(dataLoadState))
  await Promise.all([removeNotOwnedOffersPromise(getOpportunityIds(customers)), updateAccountsPromise(customers)])
  return data
}

export const fetchInitialAccountData = (serverData?: CustomerData): Promise<CustomerData> =>
  serverData
    ? Promise.resolve(serverData)
    : fetchAccountsPromise()
        .then(customers =>
          Promise.resolve({
            customers,
            offers: []
          })
        )
        .catch(err => Promise.reject(err))

export const mergeLocalWithServerOffers = async (serverData: CustomerData): Promise<CustomerData> => {
  try {
    const localOffers = await getOffersPromise()
    const offers = mergeOffers(serverData.offers || [], localOffers)
    await Promise.all(
      offers.map(offer =>
        updateOfferPromise(offer).catch(err => {
          throw err
        })
      )
    )
    return { ...serverData, offers }
  } catch (err) {
    return Promise.reject(new Error(`Error while merge local offer with server offers: ${err}`))
  }
}

export const mergeOffers = (nmsOffers: Offer[], localOffers: Offer[]): Offer[] => {
  const merged = nmsOffers.map(serverOffer => {
    const { opportunityId } = serverOffer
    const localOffer = R.find(o => o.opportunityId === opportunityId, localOffers)

    if (localOffer) {
      const { modified_client: serverTS } = serverOffer
      const { modified_client: localTS, lastSyncStarted } = localOffer
      const serverVersionIsNewer = localTS ? utc(serverTS).isSameOrAfter(localTS) : true

      const newParams = {
        clientId: opportunityId,
        lastSyncStarted
      }

      return serverVersionIsNewer ? { ...serverOffer, ...newParams } : { ...localOffer, ...newParams }
    } else {
      return serverOffer
    }
  })
  const mergedOpportunityIds = R.pluck('opportunityId', merged)
  const localOnly = R.filter(o => !mergedOpportunityIds.includes(o.opportunityId), localOffers)

  return merged.concat(localOnly)
}
